import React, { useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppData } from '../../contexts/AppDataContext';
import { Link } from 'react-router-dom';
import {
  Bars3Icon,
  MoonIcon,
  SunIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  LanguageIcon,
} from '@heroicons/react/24/outline';

const Header = ({ sidebarOpen, toggleSidebar, sidebarMobileOpen, toggleMobileSidebar }) => {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage, isRTL } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  
  const languages = [
    { code: 'fr', name: 'Français' },
    { code: 'ar', name: 'العربية' },
  ];

  return (
    <header className="h-16 bg-white/10 backdrop-blur-lg border-b border-white/20 flex items-center justify-between px-4 md:px-6 z-20">
      {/* Left side - Menu button and page title */}
      <div className="flex items-center">
        <button
          className="lg:hidden text-white/80 hover:text-white mr-4"
          onClick={toggleMobileSidebar}
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold text-white">
          Tableau de bord
        </h1>
      </div>

      {/* Right side - User menu, theme toggle, language selector */}
      <div className="flex items-center space-x-2">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10"
        >
          {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
        </button>

        {/* Language selector */}
        <Menu as="div" className="relative">
          <Menu.Button className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10">
            <LanguageIcon className="h-5 w-5" />
          </Menu.Button>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-48 bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg z-50 overflow-hidden">
              {languages.map((lang) => (
                <Menu.Item key={lang.code}>
                  {({ active }) => (
                    <button
                      onClick={() => changeLanguage(lang.code)}
                      className={`${
                        active ? 'bg-white/10' : ''
                      } ${
                        language === lang.code ? 'text-blue-400' : 'text-white'
                      } group flex w-full items-center px-4 py-2 text-sm transition-colors`}
                    >
                      {lang.name}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>

        {/* User menu */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center text-white space-x-2 p-2 rounded-full hover:bg-white/10">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-sm font-medium">
              {user?.name?.charAt(0) || "U"}
            </div>
          </Menu.Button>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-48 bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10">
                <p className="text-sm text-white">{user?.name || "Utilisateur"}</p>
                <p className="text-xs text-white/60 truncate">{user?.email || "utilisateur@example.com"}</p>
              </div>
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/settings"
                      className={`${
                        active ? 'bg-white/10' : ''
                      } text-white group flex w-full items-center px-4 py-2 text-sm transition-colors`}
                    >
                      <UserIcon className="h-4 w-4 mr-3 text-white/70" />
                      Profil
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={logout}
                      className={`${
                        active ? 'bg-white/10' : ''
                      } text-white group flex w-full items-center px-4 py-2 text-sm transition-colors`}
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-white/70" />
                      Déconnexion
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
};

export default Header; 